import React, { useEffect, useState } from 'react';
import CommonButton from '../CommonButton';

interface ScrollAdPopupProps {
    targetRef: React.RefObject<HTMLDivElement | null>;
}

const ScrollAdPopup: React.FC<ScrollAdPopupProps> = ({ targetRef }) => {
    const [isVisible, setIsVisible] = useState<boolean>(false);
    const [hasShown, setHasShown] = useState<boolean>(false);

    useEffect(() => {
        const observer = new IntersectionObserver(
            ([entry]) => {
                if (entry.isIntersecting && !hasShown) {
                    setIsVisible(true);
                }
            },
            { threshold: 0.6 }
        );

        if (targetRef.current) observer.observe(targetRef.current);
        return () => observer.disconnect();
    }, [targetRef, hasShown]);

    if (!isVisible) return null;

    return (
        <div className="fixed bottom-6 left-6 z-[999] animate-in fade-in duration-500">
            <div
                className="relative w-[300px] rounded-[28px] overflow-hidden shadow-2xl border border-white/10"
            >

                {/* ── HERO IMAGE ── */}
                <div className="relative h-[150px] overflow-hidden">
                    <img
                        src="https://plus.unsplash.com/premium_photo-1728613749980-cd3e758183df?w=600&auto=format&fit=crop&q=60&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxzZWFyY2h8OXx8Y291cG9uc3xlbnwwfHwwfHx8MA%3D%3D"
                        alt="Shopping deal"
                        className="w-full h-full object-cover block"
                    />

                    {/* Dark gradient overlay */}
                    <div
                        className="absolute inset-0"
                    />

                    {/* Top row: badge + close */}
                    <div className="absolute top-3 left-3 right-3 flex justify-between items-center z-10">
                        <span
                            className="text-white text-[10px] font-semibold tracking-widest uppercase px-3 py-1 rounded-full"
                            style={{
                                background: 'rgba(255,255,255,0.18)',
                                backdropFilter: 'blur(10px)',
                                border: '1px solid rgba(255,255,255,0.3)',
                            }}
                        >
                            Limited Offer
                        </span>

                        <button
                            onClick={() => { setIsVisible(false); setHasShown(true); }}
                            className="w-7 h-7 rounded-full flex items-center justify-center text-white text-[13px] transition-all"
                            style={{
                                background: 'rgba(0,0,0,0.3)',
                                backdropFilter: 'blur(8px)',
                                border: '1px solid rgba(255,255,255,0.2)',
                            }}
                        >
                            ✕
                        </button>
                    </div>

                    {/* Icon pill bridging hero → body */}
                    <div
                        className="absolute bottom-0 left-1/2 -translate-x-1/2 bg-white z-20 flex items-center gap-2 px-5 pt-2.5 pb-1"
                        style={{ borderRadius: '20px 20px 0 0' }}
                    >
                        <div
                            className="w-9 h-9 rounded-full flex items-center justify-center text-lg"
                            style={{
                                background: 'linear-gradient(135deg, #4a7c59, #81b08a)',
                                boxShadow: '0 4px 12px rgba(74,124,89,0.4)',
                            }}
                        >
                            🎁
                        </div>
                    </div>
                </div>

                {/* ── BODY ── */}
                <div className="bg-white px-5 pb-1 pt-1.5 text-center">
                    <h3
                        className="text-[20px] font-black leading-snug text-[#1c2b1e]"
                        style={{ fontFamily: "'Playfair Display', serif" }}
                    >
                        Saver's Secret Unlocked
                    </h3>

                    <p className="text-[12.5px] text-[#6b7a6e] mt-1 mb-3 leading-relaxed">
                        You've discovered a hidden deal just for you.
                    </p>

                    {/* Discount strip */}
                    <div
                        className="flex items-center justify-between mb-3.5 px-3.5 py-2.5 rounded-[14px]"
                        style={{
                            background: 'linear-gradient(135deg, #edf7ee, #d4edda)',
                            border: '1.5px dashed #7bc47f',
                        }}
                    >
                        <div className="text-xs uppercase tracking-widest text-[#4a7c59] font-semibold">
                            Extra Off
                        </div>
                        <div
                            className="text-base font-black leading-none text-[#2d6a4f]"
                        >
                            AED 50
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ScrollAdPopup;